import React from 'react';

import { type History } from 'history';
import { Router } from 'react-router-dom';

import FabricAnalyticsListeners from '@atlaskit/analytics-listeners/FabricAnalyticsListeners';
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import { TeamsNavigationProvider } from '@atlaskit/teams-app-internal-navigation/teams-navigation-provider';
import { useTeamsClientSetupNext } from '@atlaskit/teams-client/use-teams-client-setup-next';
import { useTeamsSentryClientSetup } from '@atlaskit/teams-client/use-teams-sentry-client-setup';
import Heartbeat from '@atlassian/heartbeat/heartbeat';
import AnalyticsProvider from '@atlassian/ptc-common/analytics-provider';
import DialogsProvider from '@atlassian/ptc-common/dialogs-provider';
import I18nProvider from '@atlassian/ptc-common/i18n-provider';
import { redirectCentralLogin } from '@atlassian/ptc-common/unauthorised';
import { useSetupAnalyticsClient } from '@atlassian/ptc-common/use-setup-analytics-client';
import { usePrincipalUser } from '@atlassian/teams/principal-user';

import ErrorBoundary from '../../common/ui/ErrorBoundary';
import { useOrgIdState } from '../../controllers/org-id';
import { useSiteStandaloneApp } from '../../controllers/site-standalone-app';
import messagesConfig from '../../i18n/messagesConfig';
import Flags from './Flags';
import AppRoutes from './Routes';
import { redirectToHome } from './utils';

export interface AppContainerProps {
	history: History;
	appName: string;
}

function AppContainer(props: AppContainerProps): React.JSX.Element | null {
	const { appName, history } = props;

	const [{ cloudId }] = useSiteStandaloneApp();
	const [{ orgId }] = useOrgIdState();
	const [{ email, principalUserId }] = usePrincipalUser();
	useTeamsSentryClientSetup(true, { appName });
	useTeamsClientSetupNext({
		cloudId,
		orgId,
		principalUserId,
	});

	const analyticsClient = useSetupAnalyticsClient(cloudId, 'site-directory');
	const { fireEvent } = useAnalyticsEvents();

	if (!analyticsClient) {
		return null;
	}

	const isRedirectEscapeHatchEnabled =
		window.location.search.includes('disable-home-redirect') ||
		fg('ptc-standalone-directory-disable-home-redirect');
	const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
	// agent/:agentId/embed or team/:teamId/embed
	const isEmbedRoute =
		(window.location.pathname.includes('/agent/') || window.location.pathname.includes('/team/')) &&
		window.location.pathname.includes('/embed');

	if (
		!isEmbedRoute &&
		!isRedirectEscapeHatchEnabled &&
		!isLocalhost &&
		// We serve Standalone in Fedramp
		!isFedRamp()
	) {
		redirectToHome({
			email,
			orgId,
			cloudId,
		});
		return null;
	}

	return (
		<I18nProvider messagesConfig={messagesConfig}>
			<FabricAnalyticsListeners
				// @ts-ignore Analytics web client types mismatch
				client={analyticsClient.getAnalyticsWebClient()}
			>
				<TeamsNavigationProvider value={{ navigate: history.push }}>
					<ErrorBoundary appName={appName} analytics={analyticsClient} fireEvent={fireEvent}>
						<AnalyticsProvider analytics={analyticsClient}>
							<DialogsProvider>
								<Router history={history}>
									<AppRoutes />
								</Router>
								<Flags />
								<Heartbeat onAuthenticationFailed={redirectCentralLogin} />
							</DialogsProvider>
						</AnalyticsProvider>
					</ErrorBoundary>
				</TeamsNavigationProvider>
			</FabricAnalyticsListeners>
		</I18nProvider>
	);
}

export default AppContainer;
